import { useFormGuardStore } from '@/backend/store/useFormGuardStore';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Loader2, Save, XOctagon } from 'lucide-react';
import { useEffect } from 'react';
import { useBlocker } from 'react-router';

interface FormGuardProps {
  isDirty: boolean;
  isValid?: boolean;
  isSubmitting?: boolean;
  onSave?: () => void;
  saveLabel?: string;
}

/**
 * FormGuard: Cristalino, rápido y sin animaciones.
 * Diseñado para que sea imposible ignorar sin guardar.
 */
export function FormGuard({
  isDirty,
  isValid = true,
  isSubmitting = false,
  onSave,
  saveLabel = 'GUARDAR',
  isHidden = false,
}: FormGuardProps & { isHidden?: boolean }) {
  const setGlobalDirty = useFormGuardStore((s) => s.setAnyFormDirty);
  const canSave = typeof onSave === 'function';

  // Sync state with Global Store
  useEffect(() => {
    setGlobalDirty(isDirty);
    return () => setGlobalDirty(false); // Cleanup on unmount
  }, [isDirty, setGlobalDirty]);

  // 1. LÓGICA DE BLOQUEO (Navigation Guard) - Instantánea
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  return (
    <>
      {/* DIÁLOGO SEGURO (Limpio & Elegante) */}
      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent className="animate-in fade-in zoom-in-95 max-w-[90%] rounded-[2.5rem] border border-zinc-100 bg-white/95 p-10 shadow-2xl backdrop-blur-xl duration-500 ease-out">
          <AlertDialogHeader className="flex flex-col items-center text-center">
            <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-full bg-amber-50 text-amber-500 ring-8 ring-amber-50/50">
              <XOctagon className="h-10 w-10" strokeWidth={2} />
            </div>
            <AlertDialogTitle className="text-2xl font-black tracking-tighter text-zinc-900 uppercase">
              sin guardar
            </AlertDialogTitle>
            <AlertDialogDescription className="mt-2 max-w-[260px] text-sm leading-relaxed font-medium text-zinc-400">
              Si sales ahora perderás todo el progreso de tu edición actual.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-3 pt-10 sm:flex-col">
            <AlertDialogCancel
              onClick={() => blocker.reset?.()}
              className="h-14 w-full rounded-2xl border-none bg-zinc-900 text-sm font-bold tracking-tight text-white shadow-lg shadow-zinc-200 transition-all hover:bg-zinc-800 active:scale-95"
            >
              Continuar editando
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => blocker.proceed?.()}
              className="h-12 w-full rounded-2xl border-none bg-transparent text-xs font-bold text-zinc-400 shadow-none transition-colors hover:text-rose-500"
            >
              Descartar y salir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* BARRA DE ADVERTENCIA (MÍNIMA & PREMIUM) */}
      {isDirty && canSave && blocker.state !== 'blocked' && !isHidden && (
        <div
          className={cn(
            'pointer-events-none fixed bottom-20 left-0 z-40 w-full px-4 pb-0',
            'lg:left-(--sidebar-width,0px) lg:w-[calc(100%-var(--sidebar-width,0px))]',
          )}
        >
          <div className="animate-in fade-in slide-in-from-bottom-8 pointer-events-auto mx-auto w-fit overflow-hidden rounded-full border border-white/20 bg-zinc-900/90 p-1.5 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] backdrop-blur-xl duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]">
            <div className="flex items-center gap-2">
              {/* Status Indicator */}
              <div className="ml-4 flex items-center gap-2 pr-2">
                <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-teal-500 shadow-[0_0_8px_rgba(20,184,166,0.8)]" />
                <span className="text-[9px] font-black tracking-widest text-zinc-400 uppercase">
                  CAMBIOS Activos
                </span>
              </div>

              {/* Action Button */}
              <Button
                type="button"
                className={cn(
                  'h-11 min-w-[160px] rounded-full bg-white px-8 text-[11px] font-black tracking-widest text-zinc-900 uppercase shadow-xl transition-all hover:bg-zinc-100 active:scale-95 disabled:opacity-50',
                  (!isValid || isSubmitting) && 'opacity-50',
                )}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!isValid || isSubmitting || !onSave) return;
                  onSave();
                }}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <div className="flex items-center gap-2">
                    <Save className="h-3.5 w-3.5" />
                    {saveLabel}
                  </div>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
